import React, { ReactNode, useEffect, useRef, useState } from "react";
import { AutoSizer } from "react-virtualized";

import { bemClassNames, getApproximateRowsCount } from "@multiweb/reactUtils";
import { FetchingState } from "@multiweb/types/react";
import { Role } from "@multiweb/uiShared/Role";
import { ResponsiveListItem } from "./ResponsiveListItem/ResponsiveListItem";

const classNames = bemClassNames("responsive-list");

export type ResponsiveListProps<T> = {
    /** Data pro vykreslení položek. */
    items: ReadonlyArray<T>;
    /** Funkce pro vykreslení položek. */
    itemRenderer: (item: T) => ReactNode;
    /** Funkce, která slouží pro načítání dalších řádků s daty. */
    loadNextItems: (count: number) => void;
    /** Funkce, která vrací pole skeletonů. */
    createSkeletonRowsKeys: (count: number) => ReadonlyArray<T>;
    /** Určuje, zda je možné načítat další data. */
    canLoadNextItems: boolean;
    /** Uchovává informaci, v jakém stavu je načítání dat. */
    fetchingState: FetchingState;
    /** Počet dat, které se právě načítají. */
    fetchingCount: number;
    /** Funkce pro vykreslení obsahu namísto položek v případě, že nejsou dostupná žádná data. */
    noRowsRenderer?: () => JSX.Element;
    /** Funkce pro generování klíčů položek. */
    keyGenerator: (item: any) => string;
    /**
     * Minimální výška řádku. Určuje se pro výpočet počtu načtených dat. Výchozí hodnotou je konstanta *DATA_ROW_MIN_HEIGHT*,
     * která je minimální výškou DataRowTemplate (použití v LiteListFrame).
     *
     * Hodnota **nesmí** být větší než je reálná minimální výška itemu. Způsobilo by to pouze částečné naplnění ResponsiveListu.
     *
     * Pokud se používá customTemplate. Měla by se tato hodnota nastavit, aby nedocházelo k zbytečnému načítání dat.
     */
    minimalItemHeight?: number;
};

/** Zobrazí pouze tolik řádků, kolik se jich do komponenty vejde. Při změně své velikosti přepočítává množství řádků k zobrazení. */
export const ResponsiveList = <T,>(props: ResponsiveListProps<T>) => (
    <AutoSizer disableWidth>{({ height }) => <ResponsiveListInner {...props} height={height} />}</AutoSizer>
);

export type ResponsiveListInnerProps<T> = ResponsiveListProps<T> & {
    /** Výška komponenty, podle které se dopočítávají další data. */
    height: number;
};

export const ResponsiveListInner = <T,>({
    items,
    itemRenderer,
    loadNextItems,
    createSkeletonRowsKeys,
    canLoadNextItems,
    fetchingState,
    fetchingCount,
    noRowsRenderer,
    keyGenerator,
    minimalItemHeight,
    height,
}: ResponsiveListInnerProps<T>) => {
    const [renderedCount, setRenderedCount] = useState<number>(1);
    const root = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (canLoadNextItems && height > 0) {
            const approximateCount = getApproximateRowsCount(height, minimalItemHeight);
            if (
                (fetchingState === FetchingState.success || fetchingState === FetchingState.initialized) &&
                items.length + fetchingCount < approximateCount
            ) {
                const loadCount = approximateCount - items.length; // Pokud načítám 4 itemy, ale vešlo by se jich 8, tak dočtení zavolám znovu. (první načítání se zruší)
                loadNextItems(loadCount);
            }
        }
    }, [canLoadNextItems, height, fetchingState, items, fetchingCount, minimalItemHeight, loadNextItems]);

    const skeletonIds = createSkeletonRowsKeys(fetchingCount);
    const renderItems = items.concat(skeletonIds);

    return (
        <div className={classNames()} ref={root} style={{ height }} role={Role.rowGroup}>
            {renderItems.slice(0, renderedCount).map((item, index) => (
                <ResponsiveListItem
                    key={keyGenerator(item)}
                    isFirst={index === 0}
                    root={root.current}
                    onShownCallback={
                        // Při iteraci způsobí rerender jen poslednímu itemu (odstraní se z něj onShownCallback).
                        index === renderedCount - 1 ? () => setRenderedCount(renderedCount + 1) : undefined
                    }
                    {...{ item, itemRenderer }}
                />
            ))}
            {items.length === 0 && fetchingState === FetchingState.success && noRowsRenderer && noRowsRenderer()}
        </div>
    );
};
